import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, CreditCard } from 'lucide-react';
import AffairesPanel from '../../ProjectDetail/panels/AffairesPanel';
import ExpensesPanel from '../../ProjectDetail/panels/ExpensesPanel';
import ProjectInvoicesPanel from '../../ProjectDetail/panels/ProjectInvoicesPanel';
import { ProjectPurchasesPanel } from '../../Expenses/ProjectPurchases';
import { apiBaseUrl, apiService } from '../../../services/api';
import { Card, Label, euros, producedHint } from '../ui';
import ProjectOffers from './ProjectOffers';
import { ErrorText, errorText, useNexusProjectRefresh, useTabError } from './shared';

// Champs d'une offre en multipart : les répartitions par itération partent en JSON, le statut peut être une option.
const appendAffaireFields = (form, data, skip) => {
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (key === 'iteration_allocations') {
      form.append('iteration_allocations_json', JSON.stringify(value));
    } else if (!skip.includes(key)) {
      form.append(key, key === 'status' && typeof value === 'object' ? value.value || String(value) : String(value));
    }
  });
  return form;
};

// Achats prévus et consommés d'après les itérations ; dépenses saisies d'après la part de chaque facture affectée au
// projet (même calcul que l'ancienne fiche).
const purchaseSummary = (iterations, expenses, projectId) => {
  const id = Number(projectId);
  const totals = { consumed: 0, forecast: 0, allocated: 0, invoices: 0 };
  iterations.forEach((iteration) => {
    const budget = Number(iteration?.budget_purchase_eur || 0);
    totals.consumed += Number(iteration?.consumed_purchase_eur || 0);
    totals.forecast += iteration?.is_purchase_forecast_overridden
      ? budget
      : Number(iteration?.forecast_purchase_eur ?? (iteration?.dont_achat || 0));
  });
  expenses.forEach((expense) => {
    const amount = Number(expense?.amount || 0);
    if (!Number.isFinite(amount) || amount === 0) return; // un avoir (négatif) rend du budget
    const allocations = Array.isArray(expense?.project_allocations) ? expense.project_allocations : [];
    const share = allocations.length
      ? allocations
        .filter((alloc) => Number(alloc?.project_id) === id)
        .reduce((sum, alloc) => sum + (Number(alloc?.percentage) || 0), 0) / 100
      : Number(Number(expense?.project_id) === id);
    if (share > 0) {
      totals.allocated += amount * share;
      totals.invoices += 1;
    }
  });
  return totals;
};

const Stat = ({ label, value, hint }) => (
  <div className="min-w-0 rounded-2xl border border-slate-200/80 bg-white px-3 py-3 shadow-sm sm:px-4">
    <Label>{label}</Label>
    <p className="mt-1 truncate text-lg font-semibold tabular-nums text-slate-900">{value}</p>
    {hint && <p className="truncate text-xs text-slate-500">{hint}</p>}
  </div>
);

const FinancesTab = ({ projectId, project, iterations = [], steering = null, refreshProject }) => {
  const navigate = useNavigate();
  const [affaires, setAffaires] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const { error, fail, attempt } = useTabError();

  const loadAffaires = useCallback(async () => {
    try {
      const data = await apiService.get(`/affaires?project_id=${projectId}`);
      setAffaires(data?.items || []);
    } catch (err) {
      fail(`Chargement des offres : ${errorText(err)}`);
    }
  }, [projectId, fail]);

  const loadExpenses = useCallback(async () => {
    try {
      const data = await apiService.get(`/expenses?project_id=${projectId}`);
      setExpenses(data?.items || []);
    } catch (err) {
      fail(`Chargement des dépenses : ${errorText(err)}`);
    }
  }, [projectId, fail]);

  useEffect(() => {
    setAffaires([]);
    setExpenses([]);
    loadAffaires();
    loadExpenses();
  }, [loadAffaires, loadExpenses]);

  useNexusProjectRefresh(projectId, (entity, action) => {
    if (entity === 'expenses') loadExpenses();
    if (entity === 'affaires' || entity === 'billing' || (entity === 'projects' && action === 'create')) loadAffaires();
  });

  // Une offre change le CA du projet : on recharge les offres et le projet.
  const afterAffaireChange = useCallback(() => {
    loadAffaires();
    refreshProject?.();
  }, [loadAffaires, refreshProject]);

  // Les erreurs d'ajout, de mise à jour et d'analyse d'achat sont affichées par AffairesPanel.
  const addAffaire = useCallback(async (affaireData, offerFile, purchaseFile) => {
    const form = new FormData();
    form.append('project_id', projectId);
    appendAffaireFields(form, affaireData, ['file_path', 'purchase_file_path']);
    if (offerFile) form.append('offer_file', offerFile);
    if (purchaseFile) form.append('purchase_file', purchaseFile);
    const created = await apiService.uploadFile('affaires', form, null, 'POST');
    afterAffaireChange();
    return created;
  }, [projectId, afterAffaireChange]);

  const updateAffaire = useCallback(async (affaireId, affaireData, offerFile = null) => {
    const skip = ['id', 'project_id', 'project_name', 'created_at', 'updated_at', 'purchase_file_path'];
    if (offerFile) skip.push('file_path');
    const form = appendAffaireFields(new FormData(), affaireData, skip);
    if (offerFile) form.append('offer_file', offerFile);
    const updated = await apiService.uploadFile(`affaires/${affaireId}`, form, null, 'PATCH');
    afterAffaireChange();
    return updated;
  }, [afterAffaireChange]);

  // Le panneau n'attend pas cette promesse : l'erreur est affichée ici, pas relancée.
  const deleteAffaire = useCallback(async (affaireId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await apiService.delete(`/affaires/${affaireId}`);
      afterAffaireChange();
    } catch (err) {
      fail(`Suppression de l'offre : ${errorText(err)}`);
    }
  }, [afterAffaireChange, fail]);

  const extractAffaireFile = useCallback((file) => attempt('Extraction IA', async () => {
    if (!file) throw new Error("Aucun fichier fourni pour l'extraction.");
    const form = new FormData();
    form.append('file', file);
    const data = await apiService.uploadFile('affaires/ai-extract', form);
    if (data?.error) throw new Error(`Erreur IA: ${data.error}`);
    const total = data?.total_ttc ?? data?.total_ht;
    return {
      number: data?.offer_number || null,
      total: total != null ? String(total) : null,
      purchase_total: null,
      hours_planned: null,
    };
  }), [attempt]);

  const extractAffairePurchase = useCallback(async (affaireId, purchaseFile) => {
    const form = new FormData();
    form.append('file', purchaseFile);
    const updated = await apiService.uploadFile(`affaires/${affaireId}/purchase-extract`, form, null, 'POST');
    afterAffaireChange();
    return updated;
  }, [afterAffaireChange]);

  const purchases = useMemo(() => purchaseSummary(iterations, expenses, projectId), [iterations, expenses, projectId]);
  const signed = Number(project?.calculated_turnover || 0);
  const invoiced = Number(project?.invoiced_turnover || 0);

  return (
    <div className="space-y-4">
      <ErrorText>{error}</ErrorText>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="CA signé" value={euros(signed)} hint={project?.status_display} />
        <Stat label="CA produit" value={steering ? euros(steering.project?.ca_produced_eur) : '—'}
          hint={steering ? `${producedHint(steering.project)}, ${euros(steering.project?.ca_to_produce_eur)} à produire` : 'Pilotage indisponible'} />
        <Stat label="CA facturé" value={euros(invoiced)}
          hint={signed > 0 ? `${Math.round((invoiced / signed) * 100)} % du signé` : 'Lignes marquées « Facturé »'} />
        <Stat label="Achats prévus" value={euros(purchases.forecast)} hint={`Consommés ${euros(purchases.consumed)}`} />
        <Stat label="Dépenses saisies" value={euros(purchases.allocated)}
          hint={`${purchases.invoices} facture${purchases.invoices > 1 ? 's' : ''}`} />
      </div>

      <ProjectOffers projectId={projectId} projectName={project?.name} />

      <Card title="Affaires & offres" icon={Briefcase}>
        <AffairesPanel
          projectId={projectId}
          affaires={affaires}
          iterations={iterations}
          apiBaseUrl={apiBaseUrl}
          onAddAffaire={addAffaire}
          onUpdateAffaire={updateAffaire}
          onDeleteAffaire={deleteAffaire}
          onAffaireFileExtract={extractAffaireFile}
          onUpdateAffairePurchaseExtract={extractAffairePurchase}
        />
      </Card>

      <ProjectInvoicesPanel projectId={projectId} iterations={iterations} maxHeight="560px" />

      <Card title="Dépenses & achats" icon={CreditCard}>
        <ExpensesPanel
          projectId={projectId}
          expenses={expenses}
          iterations={iterations}
          apiBaseUrl={apiBaseUrl}
          selectedIterationId={null}
        />
        <div className="mt-5 border-t border-slate-100 pt-4">
          <ProjectPurchasesPanel
            projectId={Number(projectId)}
            title="Articles achetés"
            onOpenInvoice={(expenseId) => navigate(`/expenses?facture=${expenseId}`)}
          />
        </div>
      </Card>
    </div>
  );
};

export default FinancesTab;
